import { useState } from "react";
import { InfoHint } from "./InfoHint";
import { Slider } from "@/components/ui/slider";
import { MagicWand, Lightning, MoonStars, Bed } from "@phosphor-icons/react";

const zone = (v) => (v >= 67 ? { c: "#00e676", l: "зелёная зона" } : v >= 34 ? { c: "#ffea00", l: "жёлтая зона" } : { c: "#ff3b30", l: "красная зона" });
const fmtBed = (b) => `${String(Math.floor(b % 24)).padStart(2, "0")}:${String(Math.round((b % 1) * 60)).padStart(2, "0")}`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function PredictorCard({ predictor }) {
  const p = predictor;
  const defStrain = p ? clamp(Math.round((p.today_strain ?? 10) * 2) / 2, 0, 21) : 10;
  const defBed = p ? clamp(Math.round(p.avg_bed * 4) / 4, 21.5, 26) : 23.5;
  const defSleep = p ? clamp(Math.round(p.avg_sleep * 4) / 4, 5, 10) : 7.5;
  const [strain, setStrain] = useState(defStrain);
  const [bed, setBed] = useState(defBed);
  const [sleep, setSleep] = useState(defSleep);
  if (!p) return null;

  const predict = (b, s, st) => clamp(Math.round(
    p.coefs[0] + p.coefs[1] * b + p.coefs[2] * s + p.coefs[3] * st + p.coefs[4] * (p.today_recovery ?? 50)
  ), 1, 99);
  const pred = predict(bed, sleep, strain);
  const baseline = predict(defBed, defSleep, defStrain);
  const delta = pred - baseline;
  const z = zone(pred);

  return (
    <div className="glass-card p-5" data-testid="predictor-card">
      <div className="flex items-center gap-2 mb-1">
        <MagicWand size={18} weight="fill" className="text-[#40c4ff]" />
        <h3 className="text-sm font-semibold">Симулятор «что если»<InfoHint text="Модель предсказывает завтрашнее восстановление по 4 факторам: сегодняшнее восстановление и нагрузка, время отбоя и длительность сна. Двигайте ползунки и смотрите, как изменится прогноз." /></h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Сегодня восстановление <span className="num font-semibold text-foreground">{p.today_recovery ?? "—"}%</span>. Что будет завтра, если…
      </p>
      <div className="flex items-start gap-6">
        <div className="text-center shrink-0 pt-1" data-testid="predictor-value">
          <div className="font-heading text-4xl font-bold num" style={{ color: z.c }}>≈{pred}%</div>
          <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: z.c }}>{z.l}</div>
          {delta !== 0 && (
            <div className="mt-1.5 text-[11px] num font-semibold" data-testid="predictor-delta"
              style={{ color: delta > 0 ? "#00e676" : "#ff3b30" }}>
              {delta > 0 ? "+" : ""}{delta}% к обычному сценарию
            </div>
          )}
        </div>
        <div className="flex-1 space-y-4">
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1 text-muted-foreground"><Lightning size={13} weight="fill" className="text-[#ffea00]" />Добью нагрузку до</span>
              <span className="num font-semibold">{strain.toFixed(1)}</span>
            </div>
            <Slider value={[strain]} onValueChange={(v) => setStrain(v[0])} min={0} max={21} step={0.5} data-testid="predictor-strain-slider" />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1 text-muted-foreground"><MoonStars size={13} weight="fill" className="text-[#a970ff]" />Лягу в</span>
              <span className="num font-semibold">{fmtBed(bed)}</span>
            </div>
            <Slider value={[bed]} onValueChange={(v) => setBed(v[0])} min={21.5} max={26} step={0.25} data-testid="predictor-bed-slider" />
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1 text-muted-foreground"><Bed size={13} weight="fill" className="text-[#40c4ff]" />Просплю</span>
              <span className="num font-semibold">{sleep.toFixed(2).replace(/\.?0+$/, "")} ч</span>
            </div>
            <Slider value={[sleep]} onValueChange={(v) => setSleep(v[0])} min={5} max={10} step={0.25} data-testid="predictor-sleep-slider" />
          </div>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-4" data-testid="predictor-confidence">
        Модель обучена на {p.n} ваших днях · точность ±{p.rmse}% · объясняет {Math.round(p.r2 * 100)}% колебаний. Это оценка, а не гарантия.
      </p>
    </div>
  );
}
